import React, { useState } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import Tooltip from "../tooltip/Tooltip";
import { AnimateSharedLayout, AnimatePresence, motion } from "framer-motion";
import Fade from "components/animations/Fade";
import HeaderSearchByNearStop from "./HeaderSearchByNearStop";
import HeaderSearchByRegion from "./HeaderSearchByRegion";
import HeaderSearchByStop from "./HeaderSearchByStop";
import HeaderSearchByArea from "./HeaderSearchByArea";
import HeaderSearch from "./HeaderSearch";
import ItineraryIcon from "./ItineraryIcon";

const schema = [
    {
        key: "favorites",
        label: "Send stops via email",
        icon: "star",
    },
    {
        key: "search",
        label: "Search",
        icon: "search",
    },
];
const itinerarySchema = [
    {
        key: "edit-itinerary",
        label: "Edit Itinerary",
        icon: "itinerary",
    },
    {
        key: "search",
        label: "Search",
        icon: "search",
    },
];
export const popupVariants = {
    open: {
        opacity: 1,
        translateY: 0,
    },
    closed: {
        opacity: 0,
        translateY: -100,
    },
};
export const motionTransition = {
    duration: 0.1,
    ease: "easeOut",
};

const iOS = ["iPhone Simulator", "iPhone"].includes(navigator.platform);

function Header(props) {
    const {
        selectedStop,
        backToItinerary,
        backToResults,
        showBackToResults,
        showBackToItinerary,
        closeModal,
        didParentRender,
        activeSchema,
        setActiveKey,
        favoriteIds,
        resultsCount,
        activeKey,
        tags,
        onSearchByRegionSubmit,
        onSearchByStopSubmit,
        onSearchByNearStopSubmit,
        onSearchByAreaSubmit,
        targetItinerary,
        isLoading,
        areaBBox,
    } = props;
    const showOverlay = ["search-by-area", "search-by-region", "search-near-stop", "search-by-stop", "search"].includes(
        activeKey,
    );
    const _schema = targetItinerary ? itinerarySchema : schema;
    const [searchByStopState, setSearchByStopState] = useState();
    return (
        <div className="trpr-sf-header__container">
            {didParentRender &&
                createPortal(
                    <AnimatePresence initial={true}>
                        {showOverlay && (
                            <motion.div
                                onClick={closeModal}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={motionTransition}
                                className="trpr-sf-header__overlay"
                            />
                        )}
                    </AnimatePresence>,
                    document.querySelector(".js-stop-finder"),
                )}
            <div className="trpr-sf-header">
                {showBackToItinerary && (
                    <button onClick={backToItinerary} className="trpr-sf-header__back-to-result">
                        <i className="trpr__icon-back" />
                        <span>Back to itinerary</span>
                    </button>
                )}
                {showBackToResults && !showBackToItinerary && (
                    <button onClick={backToResults} className="trpr-sf-header__back-to-result">
                        <i className="trpr__icon-back" />
                        <span>Back to results</span>
                    </button>
                )}
                {!showBackToResults && !showBackToItinerary && (
                    <div className="trpr-sf-header__result">
                        <i className="trpr__icon-marker" />
                        <span>{isLoading ? "0 results" : selectedStop ? "1 result" : `${resultsCount} results`}</span>
                    </div>
                )}
                {!showBackToItinerary && (
                    <AnimateSharedLayout>
                        <div className="trpr-sf-header__toolbar">
                            {_schema.map(item => {
                                const hasCounter = ["favorites", "edit-itinerary"].includes(item.key);
                                return (
                                    <motion.div
                                        transition={motionTransition}
                                        className={classNames([
                                            "trpr-sf-header__toolbar-item",
                                            { "has-counter": hasCounter },
                                        ])}
                                        key={item.key}
                                    >
                                        <motion.div
                                            transition={motionTransition}
                                            aria-hidden="true"
                                            layout="position"
                                            className="trpr-sf-header__toolbar-item-line"
                                        />
                                        <div className="trpr__tooltip-primary-wrapper">
                                            <motion.button
                                                transition={motionTransition}
                                                layout="position"
                                                key={item.key}
                                                onClick={() => setActiveKey(item.key)}
                                            >
                                                {item.icon === "itinerary" ? (
                                                    <ItineraryIcon />
                                                ) : (
                                                    <i className={`trpr__icon-${item.icon}`} />
                                                )}
                                            </motion.button>
                                            {!activeKey && !(item.key === "search" && iOS) && (
                                                <Tooltip className="is-floating trpr__mobile-alt">{item.label}</Tooltip>
                                            )}
                                        </div>
                                        <AnimatePresence>
                                            {hasCounter && favoriteIds.length > 0 && (
                                                <motion.span
                                                    layout="position"
                                                    className="trpr-sf-header__toolbar-item-counter"
                                                    transition={motionTransition}
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    exit={{ opacity: 0 }}
                                                >
                                                    {favoriteIds.length}
                                                </motion.span>
                                            )}
                                        </AnimatePresence>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </AnimateSharedLayout>
                )}
            </div>
            <div className="trpr-sf-header__secondary-container">
                <AnimatePresence initial={true}>
                    {!showBackToItinerary && activeSchema && (
                        <Fade key="active" transition={motionTransition}>
                            <div className="trpr-sf-header trpr-sf-header--secondary">
                                <div className="trpr-sf-header__active-title">
                                    {activeSchema.icon === "itinerary" ? (
                                        <ItineraryIcon />
                                    ) : (
                                        <i className={`trpr__icon-${activeSchema.icon}`} />
                                    )}
                                    <span>{activeSchema.label}</span>
                                </div>
                                <div>
                                    <button className="trpr-sf-header__active-close" onClick={closeModal}>
                                        <i className="trpr__icon-close" />
                                    </button>
                                </div>
                            </div>
                        </Fade>
                    )}
                </AnimatePresence>
            </div>
            <div className="trpr-sf-header__popup-container">
                <AnimatePresence>
                    {activeSchema && activeSchema.hasPopup && (
                        <motion.div
                            variants={popupVariants}
                            initial="closed"
                            animate="open"
                            exit="closed"
                            transition={motionTransition}
                        >
                            {activeKey === "search-by-area" && (
                                <HeaderSearchByArea
                                    areaBBox={areaBBox}
                                    onSubmit={onSearchByAreaSubmit}
                                    closeModal={closeModal}
                                    tags={tags}
                                />
                            )}
                            {activeKey === "search-near-stop" && (
                                <HeaderSearchByNearStop
                                    selectedStop={selectedStop}
                                    onSubmit={onSearchByNearStopSubmit}
                                    closeModal={closeModal}
                                    tags={tags}
                                />
                            )}
                            {activeKey === "search-by-region" && (
                                <HeaderSearchByRegion
                                    onSubmit={onSearchByRegionSubmit}
                                    tags={tags}
                                    closeModal={closeModal}
                                />
                            )}
                            {activeKey === "search-by-stop" && (
                                <HeaderSearchByStop
                                    onSubmit={onSearchByStopSubmit}
                                    closeModal={closeModal}
                                    searchByStopState={searchByStopState}
                                    setSearchByStopState={setSearchByStopState}
                                />
                            )}
                            {activeKey === "search" && (
                                <HeaderSearch setActiveKey={setActiveKey} closeModal={closeModal} />
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}

export default Header;
